import React, { ReactNode } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  OptionProps,
  ProjectDurationRow,
  ReportsFilters,
  ReportsSortBy,
} from '@models/interfaces';
import {
  getProjectDurationSortableColumn,
  renderDateValue,
  renderValue,
} from './common';

const DATE_ISSUE_TOOLTIP =
  'Start date is after the Site Acq Complete actual date. Check the Project Assigned and Site Acq Complete dates. This project is excluded from the summary and charts.';

const renderPausedDays = (value: unknown, record: ProjectDurationRow): string =>
  record.pausedTrackedSince
    ? `${String(value)} (tracked since ${renderDateValue(
        record.pausedTrackedSince,
      )})`
    : String(value);

const renderDaysWithDateIssue = (
  value: unknown,
  record: ProjectDurationRow,
): ReactNode =>
  record.hasDateIssue
    ? React.createElement(
        Tooltip,
        { title: DATE_ISSUE_TOOLTIP },
        React.createElement(
          'span',
          { className: 'prov-project-duration-report__date-issue' },
          React.createElement(WarningOutlined),
          ` ${String(value)}`,
        ),
      )
    : renderValue(value);

export const getProjectDurationColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ColumnsType<ProjectDurationRow> => [
  getProjectDurationSortableColumn(
    'totalProjectDays',
    'Total Project Days',
    ReportsSortBy.TotalProjectDays,
    filters,
    sortByOptions,
    renderDaysWithDateIssue,
  ),
  getProjectDurationSortableColumn(
    'pausedDays',
    'Total Paused Days',
    ReportsSortBy.PausedDays,
    filters,
    sortByOptions,
    renderPausedDays,
  ),
  getProjectDurationSortableColumn(
    'providenceDays',
    'Total Providence Days',
    ReportsSortBy.ProvidenceDays,
    filters,
    sortByOptions,
    renderDaysWithDateIssue,
  ),
  {
    dataIndex: 'startDate',
    key: 'startDate',
    render: renderDateValue,
    title: 'Start Date',
  },
  {
    dataIndex: 'endDate',
    key: 'endDate',
    render: renderDateValue,
    title: 'Site Acq Complete Actual',
  },
  getProjectDurationSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'oracleProjectNumbersDisplay',
    key: 'oracleProjectNumbersDisplay',
    render: renderValue,
    title: 'Oracle Project Numbers',
  },
  {
    dataIndex: 'iwmJobNumbersDisplay',
    key: 'iwmJobNumbersDisplay',
    title: 'IWM Job Numbers',
  },
  {
    dataIndex: 'paceProjectNumbersDisplay',
    key: 'paceProjectNumbersDisplay',
    render: renderValue,
    title: 'PACE Project Numbers',
  },
  getProjectDurationSortableColumn(
    'projectType',
    'Project Type',
    ReportsSortBy.ProjectType,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'projectSubtype',
    key: 'projectSubtype',
    render: renderValue,
    title: 'Project Subtype',
  },
  getProjectDurationSortableColumn(
    'projectStatus',
    'Project Status',
    ReportsSortBy.ProjectStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getProjectDurationSortableColumn(
    'progressStatus',
    'Progress Status',
    ReportsSortBy.ProgressStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getProjectDurationSortableColumn(
    'siteNumber',
    'Site Number',
    ReportsSortBy.SiteNumber,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'FALocationNumber',
    key: 'FALocationNumber',
    render: renderValue,
    title: 'FA Location Number',
  },
  {
    dataIndex: 'state',
    key: 'state',
    render: renderValue,
    title: 'State',
  },
  {
    dataIndex: 'submarket',
    key: 'submarket',
    render: renderValue,
    title: 'Submarket',
  },
  {
    dataIndex: 'towerOwner',
    key: 'towerOwner',
    render: renderValue,
    title: 'Tower Owner',
  },
];

export const getProjectDurationReportRowClassName = (
  record: ProjectDurationRow,
): string =>
  [
    record.projectBundleID
      ? 'prov-project-duration-report__row--clickable'
      : 'prov-project-duration-report__row--disabled',
    record.hasDateIssue && 'prov-project-duration-report__row--date-issue',
  ]
    .filter(Boolean)
    .join(' ');

export const getProjectDurationReportRowKey = (
  record: ProjectDurationRow,
): string =>
  [record.projectId, record.projectBundleID, record.startDate, record.endDate]
    .filter(Boolean)
    .join('-');
